import { Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import { findStateByName, overlapsAabb } from './sharedHelpers'
import { BehaviorHandler, MissionBehaviors } from './types'

/**
 * RJF03（M3 おおだま転がし）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF03.rbxl）のギミックを移植する:
 * - ②おおだま/ReturnScript: 毎フレーム監視し、Y < -50 へ落ちたら初期位置へ
 *   戻す。そのとき Hit フラグ（ゴール済み）が立っていれば消費して無音、
 *   立っていなければ Bad Machine 音（ゴールに入らず落ちた = 失敗）。
 * - その他/Goal/GoalScript: ②おおだまが Goal に触れたら（Hit が false の
 *   ときだけ）Hit = true + Winner 音。
 *
 * プレビューでの近似・意図的な差異:
 * - おおだまと Goal の接触は、Lua の Touched の代わりに AABB 重なりの
 *   毎フレーム監視で判定する（オブジェクト同士の Touched はランタイムの
 *   接触イベントに乗らないため。runtime の onTouchObject と同方式）。
 * - ゴール音（Winner）・失敗音（Bad Machine）は実音源（public/sounds/）を
 *   鳴らす（missionSfx.ts に定義。元の Workspace.Sound はアセット取得不可）。
 */

/** おおだまの落下リセットの高さしきい値（Lua: tama.Position.Y < -50）。 */
const BALL_FALL_THRESHOLD_Y = -50

/** おおだまのオブジェクト名。 */
const BALL_NAME = '②おおだま'

/** ゴール（その他/Goal）のオブジェクト名。 */
const GOAL_NAME = 'Goal'

export const createRjf03Behaviors = (): MissionBehaviors => {
  // Lua: ServerStorage.Hit（ゴール済みフラグ）。落下リセット時に消費される。
  let hit = false

  /**
   * おおだまの監視ループ（ReturnScript + GoalScript の統合）。
   * 初期位置はプレイ開始時点のおおだまの位置（Lua: local start = tama.Position）。
   */
  const watchBall: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const initial = findStateByName(world, BALL_NAME)
    if (!initial) {
      return
    }
    const ballId = initial.id
    const start: Vec3 = { ...initial.position }
    while (!ctx.signal.aborted) {
      const ball = world.getState().get(ballId)
      if (!ball) {
        return
      }
      const goal = findStateByName(world, GOAL_NAME)
      // GoalScript: おおだまがゴールに触れたら（未ゴールのときだけ）Winner。
      if (
        goal &&
        !hit &&
        overlapsAabb(ball.position, ball.size, goal.position, goal.size)
      ) {
        hit = true
        playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
      }
      // ReturnScript: Lua と同じく AssemblyLinearVelocity / AssemblyAngularVelocity
      // をゼロにしてから Position = start。ゴール済みなら無音でフラグを消費し、
      // 未ゴールなら Bad Machine（失敗音）。
      if (ball.position.y < BALL_FALL_THRESHOLD_Y) {
        world.setObjectVelocity(ballId, { x: 0, y: 0, z: 0 })
        world.setObjectAngularVelocity(ballId, { x: 0, y: 0, z: 0 })
        world.setPosition(ballId, { ...start })
        if (hit) {
          hit = false
        } else {
          playSample(
            FAIL_GAMEOVER_SFX.url,
            FAIL_GAMEOVER_SFX.volume,
            ctx.signal,
          )
        }
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return { onPlay: [watchBall] }
}
